import usePreviousDuringAnimation from '@hooks/use-previous-during-animation';
import useSyncEffect from '@hooks/use-sync-effect';
import type { Conversation } from '@interfaces/conversation';
import MiddleHeader from '@pages/ChatFeed/MiddleColumn/MiddleHeader';
import MiddleSearch from '@pages/ChatFeed/MiddleColumn/MiddleSearch';
import buildClassName from '@utils/build-class-name';
import type { FC } from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useActiveConversation, useHasActiveMiddleSearch, useSharedActions } from '@/newstore/selectors/shared';

import Composer from './Composer';
import { EDITABLE_INPUT_CSS_SELECTOR, EDITABLE_INPUT_ID } from './constants';
import MessageList from './MessageList';
import styles from './MiddleColumn.module.scss';
import ScrollDownButton from './ScrollDownButton';

interface StateProps {
  conversation: Conversation;
  hasActiveMiddleSearch: boolean;
}

const NonMemoMiddleColumn: FC<StateProps> = ({ conversation, hasActiveMiddleSearch }) => {
  const middleColumnRef = useRef<HTMLDivElement>(null);
  const [isFabShown, setIsFabShown] = useState<boolean>();
  const [isNotchShown, setIsNotchShown] = useState<boolean>();
  const renderingIsFabShown = usePreviousDuringAnimation(isFabShown, 0);

  const canPost = conversation.is_authorized;
  const messagingDisabled = !canPost;

  useSyncEffect(() => {
    setIsNotchShown(undefined);
  }, [conversation.id]);

  const footerClassName = buildClassName(
    styles.footer,
    !canPost && styles.noComposer,
    canPost && isNotchShown && 'with-notch',
  );

  return (
    <div ref={middleColumnRef} id="middle-column">
      <MiddleHeader />
      <div className={styles.columnWrapper}>
        {conversation.id && (
          <>
            <div className={buildClassName('messages-layout', styles.messagesLayout)}>
              <MessageList
                conversation={conversation}
                canPost={canPost}
                onFabToggle={setIsFabShown}
                onNotchToggle={setIsNotchShown}
              />
              <div className={footerClassName}>
                {canPost && (
                  <Composer
                    editableInputId={EDITABLE_INPUT_ID}
                    editableInputCssSelector={EDITABLE_INPUT_CSS_SELECTOR}
                    conversationId={conversation.id}
                    conversationLanguageCode={conversation.language_code}
                    containerRef={middleColumnRef}
                  />
                )}
                {messagingDisabled && <div className={styles.messagingDisabled}>DISABLED</div>}
              </div>
              <ScrollDownButton isShown={renderingIsFabShown || false} />
            </div>
          </>
        )}
      </div>
      <MiddleSearch isActive={hasActiveMiddleSearch} />
    </div>
  );
};

const MiddleColumn = memo(function MiddleColumn() {
  const { conversationId } = useParams();
  const { setActiveConversationId } = useSharedActions();
  useEffect(() => {
    if (conversationId) {
      setActiveConversationId(Number(conversationId));
    }
  }, [conversationId, setActiveConversationId]);

  const { conversation, isLoading } = useActiveConversation();
  const hasActiveMiddleSearch = useHasActiveMiddleSearch();

  if (isLoading) {
    return 'Syncing conversations...';
  }

  return conversation ? (
    <NonMemoMiddleColumn conversation={conversation} hasActiveMiddleSearch={hasActiveMiddleSearch} />
  ) : null;
});

export default MiddleColumn;
